import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {PanelBaseComponent} from '../panels/panel-base.component';
import {UserSettingsService} from '../user-settings.service';
import {MessageBusService} from '../message-bus.service';
import {OrionSettingsService} from '../orion-settings-dialog/orion-settings.service';
import {HgNormalizationService} from '../hedging-grid/services/hg-normalization.service';
import {ApgPortfolio} from '../adjustment-pricing-grid/model/ApgPortfolio';
import {CopyOrdersToService} from '../adjustment-pricing-grid/services/copy-orders-to.service';
import {PositionsAfterStateService} from '../adjustment-pricing-grid/services/positions-after-state.service';
import {MavenCardPricingService} from './results/maven-card-pricing.service';
import {MavenAppliedMarksService} from './results/maven-applied-marks.service';
import {MavenCard, MavenResultReady} from './results/maven-delivery.contract';
import {MavenRunSettled, MavenSetupComponent} from './setup/maven-setup.component';
import {
    isKnownMavenStage,
    isMavenSupplierStage,
    MAVEN_STAGE_CLOSE,
    mavenStageLabel,
    mavenSupplierPhaseLabel
} from './results/maven-run-stages';

// THE MAVEN PANEL CHASSIS (CR-20260910-001 milestone 4, lane S6).
//
// Maven orders a roll from the APG ORION, hedges from the ORION (OPG) per side and a close from
// the Hedge Matrix ORION Close, composes one package per candidate day, and shows the ranked
// cards. This class is the SHELL only: three columns, the panel lifecycle, and nothing else.
//
// THE THREE COLUMNS AND WHO OWNS THEM
//   left   Setup    — lane S6 (this lane). The Kanban THE SETUP column is its definition.
//   middle Results  — lane S7, behind <ets-maven-results>.
//   right  Details  — lane S7, behind <ets-maven-details>.
//
// ⚠ THE TWO CHILD TAGS ARE A SEAM, NOT A DETAIL. They are placed here on day one, before lane S7
// writes a line, and their components are registered in maven.module.ts from the same commit. S7
// fills `results/` and `details/` — the folders it owns — and NEVER edits this template or that
// module. That is the whole reason the empty shells exist: a placeholder that is not registered
// would force S7 to come back into this lane's files to register it, which is exactly the
// concurrent edit the lane split exists to prevent. Additions to the module are requested from
// S6 through the leader (build plan section 6).
//
// SESSION ONLY (ruling R-I, contract M1 section 8). Nothing about a Maven run is persisted — not
// the Setup inputs, not the cards, not the ranking, not the selection. Only the transaction the
// trader actually sends survives. `getState` therefore returns NOTHING and `setState` reads
// nothing: persistence is what happens by DEFAULT here, because PanelBaseComponent.saveState()
// writes every panel's state through UserSettingsService, so the rule has to be written down in
// code rather than assumed. A trader who closes the panel and re-opens it finds it empty, and
// that is correct — the market has moved and a fresh run would price differently anyway.
//
// NO STOP BUTTON (ruling R-D). There is no cancel command in the contract and no stop control in
// this panel. Pressing Apply again is the only way a run in flight ends early: it supersedes.
@Component({
    selector: 'ets-maven-panel',
    templateUrl: './maven-panel.component.html',
    styleUrls: ['./maven-panel.component.scss'],

    // ⚠ THIS LINE IS WHY ANOTHER PANEL OPENED BLANK AFTER MAVEN, and it fixes a defect this
    // component's own SCSS diagnosed correctly and then could not act on. The comment in
    // maven-panel.component.scss says: gating only `.panel-container` leaves the HOST at
    // height:100%, so a panel added AFTER this one is pushed out of the overflow-hidden workspace
    // viewport and renders blank. The rule it wrote, `:host.ets-hidden`, was right — but
    // `ets-hidden` was bound on the INNER div in the template, so the class never reached the host
    // and the rule could never fire.
    //
    // A CORRECT RULE GUARDING A SELECTOR NOTHING MATCHES IS INDISTINGUISHABLE FROM A WORKING ONE
    // — in the stylesheet, in review, and in every gate. It reads as protection and provides none.
    //
    // The class must be bound on the HOST, from here, exactly as PriceChartPanelComponent and
    // CollarsComponent already do. The inner binding stays: it hides the content, this collapses
    // the box.
    host: {'[class.ets-hidden]': '!isActive'},
    changeDetection: ChangeDetectionStrategy.OnPush,

    // ⚠ PROVIDED HERE, ON THE PANEL — NOT IN maven.module.ts, AND THAT IS THE WHOLE POINT.
    //
    // Both services hold PER-RUN state: the marks store is a Map of which cards have been ordered
    // from, and the pricing service holds this run's cards, its pricing context and its live quote
    // subscriptions. This module is eager, like every panel module in this app, so a provider
    // listed in it hoists to the ROOT injector and becomes ONE instance for the whole application
    // — exactly the hazard maven.module.ts warns about in its own header.
    //
    // Maven is explicitly MULTI-INSTANCE: `panelInstanceId` is an input on the Setup and the
    // backend scopes supersede by it. So a root singleton would mean two open Maven panels sharing
    // one marks Map and one set of cards — panel A's green A mark appearing on panel B's card, and
    // the second panel's run clobbering the first's quote subscriptions. Nothing would throw and
    // nothing would log; both panels would simply render each other's state.
    //
    // Declared on the PANEL rather than on Results because the Details column injects the marks
    // store too, and the two columns must see the same instance. One store per panel, which is
    // what "session only" (R8) and the per-card marks (D6) actually mean.
    providers: [
        MavenCardPricingService,
        MavenAppliedMarksService,

        // ⚠ MAVEN'S OWN INSTANCES, AND THAT IS THE WHOLE POINT — NOT the root ones.
        //
        // The adjustment pop-up threw on the first line of `show()`:
        //   TypeError: Cannot read properties of undefined (reading 'orientation')
        //     at CopyOrdersToService.getStorageKey (copy-orders-to.service.ts:526)
        // `_serviceConfig` was undefined because nobody had configured it for Maven, and the
        // decorator around `show()` uses `finally` rather than `catch`, so the throw propagated and
        // the dialog never rendered. maven.module.ts:49 documented this hazard before either column
        // was built and named this file; it was simply never requested.
        //
        // These are declared HERE, on the panel, and not in maven.module.ts: every panel module in
        // this app is eager, so a module provider hoists to the ROOT injector. Adding them there
        // would hand Maven an instance the APG or Package Comparison had configured — or repoint
        // theirs to Maven's portfolio. That is worse than no provider at all, and it is the same
        // hazard as the two per-run services above. A component provider does not hoist, and the
        // pop-ups resolve through the element injector: pop-up -> Details -> this panel.
        //
        // They arrive WITH the code that configures them (`configureSavedStateReaders` below) —
        // the rule maven.module.ts states in its own header.
        CopyOrdersToService,
        PositionsAfterStateService
    ]
})
export class MavenPanelComponent extends PanelBaseComponent {

    @ViewChild(MavenSetupComponent) private _setup: MavenSetupComponent;

    private _viewReady = false;
    private _destroyed = false;
    private _portfolioLoadStarted = false;
    private _portfolioLoadFrame: number | null = null;

    // ---- THE SEAM BETWEEN THE THREE COLUMNS ----------------------------------------------------
    //
    // ⚠ WHY THIS STATE IS HERE. The Setup ran the skill and kept the result in its own fields; the
    // template placed <ets-maven-results> and <ets-maven-details> with NO BINDINGS. So `delivery`
    // was permanently null, `cards` permanently empty, and a run that delivered five cards rendered
    // an empty column. Both halves were correct on their own and nothing joined them.
    //
    // It lives on the PANEL because the panel is the only thing that sees all three columns. Setup
    // emits, the panel holds, Results and Details read.
    //
    // Session only (R-I): these are plain fields on a component that persists nothing. `getState`
    // returns null below, so none of this survives a logout — which is the rule, not an oversight.

    running = false;

    stagesSeen: string[] = [];

    delivery: MavenResultReady | null = null;

    selectedCard: MavenCard | null = null;

    portfolio: ApgPortfolio | null = null;

    constructor(
        changeDetector: ChangeDetectorRef,
        userSettingsService: UserSettingsService,
        messageBus: MessageBusService,
        private readonly _orionSettings: OrionSettingsService,
        private readonly _normalization: HgNormalizationService,
        private readonly _copyOrdersTo: CopyOrdersToService,
        private readonly _positionsAfterState: PositionsAfterStateService,
        private readonly _marks: MavenAppliedMarksService
    ) {
        super(changeDetector, userSettingsService, messageBus);
    }

    /**
     * Point Maven's saved-state readers at the PORTFOLIO'S OWNER, not the logged-in user.
     *
     * Ruling R-E: a super user may run on another trader's portfolio, and every saved-state read in
     * this panel follows the portfolio rather than the login. This is the same configuration
     * ApgDataService applies on each of its own calls (apg-data.service.ts:98-104).
     *
     * `orientation` is left undefined deliberately — Maven is neither side of the Package Comparison,
     * and both services fall through to their default storage key when it is unset.
     *
     * Called the moment a portfolio is chosen, so there is never a window in which these are
     * resolvable but unconfigured — which is precisely the state that threw.
     */
    private configureSavedStateReaders(portfolio: ApgPortfolio | null): void {

        if (!portfolio) {
            return;
        }

        const config = {
            orientation: undefined,
            userId: portfolio.userId,
            userName: portfolio.userName
        };

        this._copyOrdersTo.configure(config);
        this._positionsAfterState.configure(config);
    }

    /**
     * Maven's OWN "show top N" setting (MAVEN Settings dialog, owner 2026-09-14 - it used to borrow
     * the APG ORION's), applied by the Results column as a DISPLAY limit — never a cut on what
     * arrived (R1). The run delivers every day it worked out and the whole array stays in memory
     * with its ranks and its subscriptions; this only decides how many cards are drawn.
     */
    get topN(): number | null {
        const value = this._orionSettings ? this._orionSettings.mavenShowTopN : null;

        return (value === null || value === undefined) ? null : value;
    }

    /**
     * Point-value ratio for a leg's asset against the portfolio's, so an ES leg prices beside an SPX
     * one. An arrow property, not a method: it is passed as a value into the Results column and must
     * keep this `this`.
     *
     * Null portfolio or matching asset means 1, which is correct for every run with no cross-asset
     * leg — the common case, and the one that would hide a mistake here.
     */
    getNormFactor = (asset: string): number => {
        if (!asset || !this.portfolio || !this.portfolio.asset || asset === this.portfolio.asset) {
            return 1;
        }

        return this._normalization.getPointValueRatio(asset, this.portfolio.asset);
    }

    /**
     * The spinner's text while the run works: the latest stage reached, by its label. Before the
     * first stage event arrives it is the stock "Loading...". Never a number (ruling L28). A stage
     * whose constant the backend renamed resolves to no label and is skipped — an identifier is
     * never put on screen in its place (ruling L33).
     */
    get runningMessage(): string {

        const seen = (this.stagesSeen || []).filter(s => isKnownMavenStage(s));

        if (seen.length === 0) {
            return 'Loading...';
        }

        const latest = seen[seen.length - 1];

        // The supplier phase is one message for its three stages (see mavenSupplierPhaseLabel).
        if (isMavenSupplierStage(latest)) {
            return mavenSupplierPhaseLabel(seen.indexOf(MAVEN_STAGE_CLOSE) >= 0);
        }

        return mavenStageLabel(latest);
    }

    onRunningChange(running: boolean): void {
        this.running = running;
        this._changeDetector.detectChanges();
    }

    onStagesChange(stages: string[]): void {
        this.stagesSeen = stages || [];
        this._changeDetector.detectChanges();
    }

    /**
     * A run settled. Assembles the delivery the two columns read.
     *
     * Null clears the column — that is what a fresh Apply emits, and it is how the previous run's
     * cards leave the screen before the new one's arrive. A DISPLACED run never reaches here: the
     * Setup's supersede arm returns without settling, so a late result cannot blank the column that
     * replaced it.
     *
     * `strategyType` rides along as delivered (ruling L39) and is NEVER defaulted here. It was the
     * one field the frontend dropped — on the wire as ProtoMember(7), absent from the TS mirror — and
     * the Details column's two order controls were dead because of it. If it ever arrives null the
     * controls stay disabled and say so; an absent strategy type must not become a guessed one,
     * because both ways of getting it wrong are silent.
     */
    onRunSettled(settled: MavenRunSettled | null): void {

        if (!settled) {
            this.delivery = null;
            this.selectedCard = null;
            this._changeDetector.detectChanges();
            return;
        }

        this.delivery = {
            topic: null,
            runId: null,
            outcome: settled.outcome,
            failureDetail: settled.failureDetail,
            defaultQty: settled.defaultQty,
            strategyType: settled.strategyType,
            existingHedges: settled.existingHedges,
            cards: settled.cards as MavenCard[],
            expectedRangeNotes: settled.expectedRangeNotes || []
        };

        this.selectedCard = null;

        // The marks store needs the run's cards to re-derive an A mark from the saved state.
        this._marks.attachRun(this.delivery.cards, this.portfolio, this.delivery.strategyType);

        this._changeDetector.detectChanges();
    }

    onPortfolioChange(portfolio: ApgPortfolio | null): void {
        this.portfolio = portfolio;
        // Before anything can open a pop-up against it.
        this.configureSavedStateReaders(portfolio);
        this._marks.attachRun(this.delivery ? this.delivery.cards : [], portfolio, this.delivery ? this.delivery.strategyType : null);
        this._changeDetector.detectChanges();
    }

    onCardPicked(card: MavenCard | null): void {
        this.selectedCard = card;
        this._changeDetector.detectChanges();
    }

    etsOnInit(): void {
    }

    etsOnDestroy(): void {
        this._destroyed = true;
        this.cancelPortfolioLoad();
    }

    etsAfterViewInit(): void {
        this._viewReady = true;
        this.schedulePortfolioLoad();
    }

    protected onIsActiveChanged(): void {
        this.cancelPortfolioLoad();
        this.schedulePortfolioLoad();
    }

    private cancelPortfolioLoad(): void {
        if (this._portfolioLoadFrame !== null) {
            cancelAnimationFrame(this._portfolioLoadFrame);
            this._portfolioLoadFrame = null;
        }
    }

    private schedulePortfolioLoad(): void {
        if (!this._viewReady || this._destroyed || !this.isActive || this._portfolioLoadStarted) {
            return;
        }

        // The parent view includes all three columns and Setup's controls. A frame callback
        // runs BEFORE paint: render the active panel in the first, then start loading in the
        // next so the browser can paint the complete empty panel between them.
        this._portfolioLoadFrame = requestAnimationFrame(() => {
            this._changeDetector.detectChanges();
            this._portfolioLoadFrame = requestAnimationFrame(() => {
                this._portfolioLoadFrame = null;
                if (this._destroyed || !this.isActive) {
                    return;
                }
                this._portfolioLoadStarted = true;
                void this._setup.loadInitialPortfolio();
            });
        });
    }

    // Session only (R-I): nothing is saved. Returning a falsy state is what actually stops the
    // write — PanelBaseComponent.saveState() returns early on a falsy getState(), so no key is
    // ever written for this panel and none is left behind to be read at the next login.
    protected getState(): any {
        return null;
    }

    // Session only (R-I): nothing is restored. Kept as an explicit no-op with this comment rather
    // than left to the base class, so that a future reader sees the rule and not an omission.
    protected setState(state: any) {
    }
}
